import React, { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import './App.css';
import { Global } from '@emotion/react';
import AccountsPage from './pages/AccountsPage';
import ApproveDemoPage from './pages/ApproveDemoPage';
import ContactUsPage from './pages/ContactUsPage';
import FaqPage from './pages/FaqPage';
import ForgetPasswordPage from './pages/ForgetPasswordPage';
import HomePage from './pages/HomePage';
import Login from './pages/LoginPage';
import NewAccountPage from './pages/NewAccountPage';
import NewReportPage from './pages/NewReportPage';
import RegisterPage from './pages/RegisterPage';
import RequestDemoPage from './pages/RequestDemoPage';
import Tutorial from './pages/Tutorial';
import UserProfilesPage from './pages/UserProfilesPage';
import PlansPage from './pages/PlansPage';
import AnnouncementPage from './pages/AnnouncementPage';
import { SettingsPage } from './pages/SettingsPage';
import { MahjongGameRecordPage } from './pages/MahjongGameRecordPage';
import { DiscoverPage } from './pages/DiscoverPage';
import NoMatch from './pages/NoMatch'
import { createTheme, SxProps, ThemeProvider } from "@mui/material/styles";
import SimpleAppBar from './components/AppBar';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent, FormControl, InputLabel, Select, MenuItem, Box, SwipeableDrawer, Typography, RadioGroup, FormControlLabel, Radio, Container } from '@mui/material';
import { styled } from "@mui/material/styles";
import { grey } from "@mui/material/colors";

const theme = createTheme({
  typography: {
    "fontFamily": `"cwTeXYen", 'Quicksand'`,
    "fontSize": 16,
    "fontWeightLight": 500,
    "fontWeightRegular": 500,
    "fontWeightMedium": 500
  },
  palette: {
    primary: {
      main: '#093a4f',
    },
    secondary: {
      main: '#d2e6d5',
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#0b4761",
          fontSize: "16px"
        },
        arrow: {
          color: "#0b4761",
        }
      }
    }
  }
});

const languages = [
  { value: "zh", name: "中文" },
  { value: "en", name: "Eng" },
  // { value: "th", name: "ไทย" },
]

interface LangSelectProps {
    lang: string,
    setLang: (e: SelectChangeEvent) => void,
    sx?: SxProps,
}

interface LangRadioProps {
  lang: string,
  setLang: (e: React.ChangeEvent<HTMLInputElement>) => void,
  sx?: SxProps,
}

function LanguageButton(props: LangSelectProps) {
  return (
    <FormControl variant="standard" sx={props.sx}>
      <InputLabel id="lang-select-label">Language</InputLabel>
      <Select
        id="lang-select"
        value={props.lang}
        onChange={props.setLang}
        color='info'
      >
        {
          languages.map(lang => {
            return (
              <MenuItem key={lang.value} value={lang.value}>{lang.name}</MenuItem>
            )
          })
        }
      </Select>
    </FormControl>
  )
}

const StyledBox = styled(Box)(({theme}) => ({
  width: 130,
  float: "right",
  backgroundColor: theme.palette.mode === "light" ? "#fff" : grey[800],
}));

const Puller = styled(Box)(({theme}) => ({
  width: 30,
  height: 6,
  backgroundColor: theme.palette.mode === "light" ? grey[300] : grey[900],
  borderRadius: 3,
  position: "absolute",
  top: 8,
  left: "calc(50% - 15px)",
}));

const drawerBleeding = 50;
function LanguageDial(props: LangRadioProps) {
  const [open, setOpen] = useState(false);
  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  }

  return (
    <>
      <Global
        styles={{
          '.MuiDrawer-root > .MuiPaper-root': {
            width: 114,
            // height: `calc(50% - ${drawerBleeding}px)`,
            height: "auto",
            overflow: 'visible',
          },
          '.PrivateSwipeArea-root': {
            width: 125,
            marginLeft: "auto",
            marginRight: "4%",
          }
        }}
      />
      
      <Container 
        component="span"
        onMouseEnter={toggleDrawer(true)}
        sx={{
          width: "auto",
        }}
      >
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={toggleDrawer(false)}
          onOpen={toggleDrawer(true)}
          swipeAreaWidth={drawerBleeding}
          disableSwipeToOpen={false}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: "auto",
              marginLeft: "auto",
              marginRight: "5%",
            }
          }}
        >
          <StyledBox
            sx={{
              position: "absolute",
              top: -drawerBleeding,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              visibility: "visible",
              color: "text.secondary",
            }}
          >
            <Puller/>
            <Typography
              sx={{
                p: 2,
              }}
            >
              Languages
            </Typography>
          </StyledBox>
          <StyledBox
            sx={{
              px: 1,
              pb: 2,
              width: "100%",
              height: "100%",
              overflow: "auto",
            }}
            onMouseLeave={toggleDrawer(false)}
          >
            <RadioGroup
              value={props.lang}
              name="lang-radio"
              onChange={props.setLang}
            >
              {
                languages.map(lang => 
                    <FormControlLabel key={lang.value} value={lang.value} control={<Radio color="default"/>} label={lang.name}/>
                )
              }
            </RadioGroup>
          </StyledBox>
        </SwipeableDrawer>
      </Container>
    </>
  )
}

export default function App(props: { langButt?: JSX.Element }) {
  // let search = window.location.search;
  // let params = new URLSearchParams(search);
  // let apiToken = params.get('api_token');
  // let uid = params.get('uid');
  // let isDemo = params.get('demo');

  // Appbar related
  const [title, setTitle] = useState<string>("");
  const [showBottom, setShowBottom] = useState<boolean>(true);

  // Language Translation
  const { i18n } = useTranslation();
  const [lang, setLang] = useState<string>(i18n.language)
  
  const handleLangChange = (e: any) => {
    // console.log(e.target.value)
    setLang(e.target.value);
    i18n.changeLanguage(e.target.value);
  }

  const langButt_appbar = <LanguageButton lang={lang} setLang={handleLangChange} sx={{ minWidth: 70, backgroundColor: "white"}} />
  const langButt_general = <LanguageDial lang={lang} setLang={handleLangChange} />
  useEffect(() => {
    setShowBottom(true);
    if (title === "reports") {
      setShowBottom(false);
    }
  }, [title])

  useEffect(() => {
      // console.log(process.env.NODE_ENV);
      // console.log(process.env.REACT_APP_ENVIRONMENT);
  }, [])
  // if (apiToken !== null && uid !== null) {
  //   let tempUser = {
  //     id: parseInt(uid),
  //     email: "",
  //     api_token: apiToken,
  //     roles_names: ["temp_user"],
  //     permissions_names: ["view_full_report", "view_simple_report"],
  //     pin: ""
  //   } as User;
  //   dispatch(loggedIn(tempUser));
  // } else if (isDemo === 'true') {
  //   let tempUser = {
  //     id: 3,
  //     email: "",
  //     api_token: apiToken,
  //     roles_names: ["demo"],
  //     permissions_names: ["view_full_report", "view_simple_report"],
  //     pin: ""
  //   } as User;
  //   dispatch(loggedIn(tempUser));
  // }
  return (
    <ThemeProvider theme={theme}>
      <div className="App">
          {title !== "notShow" && <SimpleAppBar title={title} bottom={showBottom} langButt={langButt_appbar}/> }
          <Routes>
            <Route path="/" element={<HomePage setTitle={setTitle}/>} />
            <Route path="/users" element={<UserProfilesPage setTitle={setTitle}/>} />
            <Route path="/contactus" element={<ContactUsPage setTitle={setTitle}/>} />
            <Route path="/faq" element={<FaqPage setTitle={setTitle}/>} />
            <Route path="/tutorials" element={<Tutorial setTitle={setTitle}/>} />
            <Route path="/logout" element={<UserProfilesPage setTitle={setTitle}/>} />
            <Route path="/login" element={<Login setTitle={setTitle} langButt={langButt_general} />} />
            <Route path="/forgetpassword" element={<ForgetPasswordPage setTitle={setTitle} langButt={langButt_general}/>} />
            <Route path="/plans" element={<PlansPage setTitle={setTitle} langButt={langButt_general} />} />
            <Route path="/discover" element={<DiscoverPage setTitle={setTitle}/>} />
            <Route path="/register" element={<PlansPage setTitle={setTitle}/>} />
            <Route path="/centerregister" element={<RequestDemoPage setTitle={setTitle}/>} />
            <Route path="/requestdemo" element={<RegisterPage setTitle={setTitle} langButt={langButt_general} />} />
            <Route path="/approvedemo" element={<ApproveDemoPage setTitle={setTitle}/>} />
            <Route path="/report" element={<NewReportPage setTitle={setTitle}/>} />
            <Route path="/accounts" element={<AccountsPage setTitle={setTitle}/>} />
            <Route path="/newaccount" element={<NewAccountPage setTitle={setTitle} langButt={langButt_general} />} />
            <Route path="/announcement" element={<AnnouncementPage setTitle={setTitle}/>} />
            <Route path="/settings/:section" element={<SettingsPage setTitle={setTitle}/>} />
            <Route path="/mahjong/:matchId" element={<MahjongGameRecordPage setTitle={setTitle}/>} />
            <Route path="*" element={<NoMatch setTitle={setTitle}/>} />
            
            {/* <Route path="/reports/game" element={<GameReport/>} />
            <Route path="/reports/mission" element={<MissionReport/>} />
            <Route path="/reports/full" element={<FullReportPage/>} />
            <Route path="/register" element={<RegisterPage/>} />
            <Route path="/requestdemo" element={<RequestDemoPage/>} /> */}
          </Routes>
      </div>
    </ThemeProvider>
  );
}